import {Box} from '@mantine/core';
import React, {useState} from 'react';

import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {BioentityLinkFieldWithTargetSchema} from 'schemas/fields/SchemaFieldConstants';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import {StudySearchItem} from 'studies/StudySearchInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

import CreateLinkableBioentityForm, {
  getCreateFormValidation,
  isSelectedEntityLinkField,
  EntityLinkFields,
  EntityLinkFieldOrDummy,
  UNSELECTED_DUMMY_OBJECT,
} from './CreateLinkableBioentityForm';

/**
 * Payload returned when the user successfully submits the create entity modal.
 * Contains all the information needed to create a new bioentity.
 */
export interface ConfirmCreateLinkableBioentityPayload {
  /** The name for the new entity */
  name: string;
  /** The folder ID where the entity should be created */
  folderId: string;
  /** The selected entity link field, if any */
  entityLinkField?: BioentityLinkFieldWithTargetSchema | null;
  /** The schema ID to apply to the entity, if any */
  tagSchemaId?: string;
  /** IDs of studies to associate with the entity, if any */
  studyIds?: Array<string>;
}

/**
 * Props for the CreateLinkableBioentityModal component.
 */
export interface CreateLinkableBioentityModalProps {
  /** The title displayed in the modal header */
  title: string;
  /** Optional description text displayed at the top of the modal content */
  description?: string;
  /** Initial folder ID to pre-select in the folder selector */
  initialFolderId?: string;
  /** Placeholder text for the entity name input */
  namePlaceholder?: string;
  /** Callback invoked when the user confirms the creation with valid data */
  onRequestSubmit: (result: ConfirmCreateLinkableBioentityPayload) => void;
  /** Callback invoked when the user cancels or closes the modal */
  onRequestClose: () => void;
  /** Optional list of entity link fields to display in the form */
  entityLinkFields?: EntityLinkFields;
  /** Types used to filter available schemas in the schema selector */
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType>;
  /** Label for the entity link field dropdown */
  entityLinkFieldLabel?: string;
}

/**
 * A modal dialog for creating bioentities (e.g., AA sequences, DNA sequences).
 *
 * This modal wraps the CreateLinkableBioentityForm component and manages its state internally.
 * It handles form submission, validation, and provides Cancel/Create buttons.
 *
 * Key features:
 * - Manages form state internally (name, folder, schema, entity links, studies)
 * - Validates form before enabling the Create button
 * - Persists the selected folder to user defaults
 * - Calls onRequestSubmit with the complete form data when the user clicks Create
 *
 * @example
 * ```tsx
 * <CreateLinkableBioentityModal
 *   title="Create AA sequence"
 *   initialFolderId="lib_12345"
 *   oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
 *   entityLinkFieldLabel="Translation link"
 *   onRequestSubmit={(result) => {
 *     // Create the entity with result.name, result.folderId, etc.
 *   }}
 *   onRequestClose={() => {
 *     // Close the modal
 *   }}
 * />
 * ```
 */
export default function CreateLinkableBioentityModal({
  title,
  description,
  initialFolderId,
  namePlaceholder,
  onRequestSubmit,
  onRequestClose,
  entityLinkFields = [],
  oligoAwareFolderItemTypes,
  entityLinkFieldLabel,
}: CreateLinkableBioentityModalProps): JSX.Element {
  const [name, setName] = useState(namePlaceholder ?? '');
  const [folderId, setFolderId] = useState<Optional<string>>(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [tagSchemaId, setTagSchemaId] = useState<string | null>(null);
  const [selectedEntityLinkField, setSelectedEntityLinkField] =
    useState<EntityLinkFieldOrDummy>(UNSELECTED_DUMMY_OBJECT);
  const [selectedStudyItems, setSelectedStudyItems] = useState<Array<StudySearchItem>>([]);

  const {interfaceIdsBySchemaId} = useSchemaSSIsByIdQuery();

  const {isValid} = getCreateFormValidation({
    name,
    folderId,
    selectedEntityLinkField,
    entityLinkFields,
    tagSchemaId,
    interfaceIdsBySchemaId,
  });

  const handleConfirm = (): void => {
    const selectedEntityLinkFieldValue: BioentityLinkFieldWithTargetSchema | null = isSelectedEntityLinkField(
      selectedEntityLinkField
    )
      ? selectedEntityLinkField
      : null;
    const confirmPayload: ConfirmCreateLinkableBioentityPayload = {
      folderId: folderId!,
      name,
      entityLinkField: selectedEntityLinkFieldValue,
    };
    if (tagSchemaId != null) {
      confirmPayload.tagSchemaId = tagSchemaId;
    }
    if (selectedStudyItems.length > 0) {
      confirmPayload.studyIds = selectedStudyItems.map((studyItem) => studyItem.id);
    }
    onRequestSubmit(confirmPayload);
    onRequestClose();
  };

  const handleFolderIdChange = (newFolderId: string): void => {
    setFolderId(newFolderId);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
  };

  const buttons = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Create',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: !isValid,
      onClick: handleConfirm,
      dataTestAttrs: {component: 'CreateLinkableBioentityModal', element: 'submitButton'},
    },
  ];

  return (
    <Modal
      title={title}
      showHeaderBorder={false}
      enableRedesign={true}
      onRequestClose={onRequestClose}
      buttons={buttons}
    >
      {description != null && <Box pb="sm">{description}</Box>}
      <CreateLinkableBioentityForm
        name={name}
        folderId={folderId}
        tagSchemaId={tagSchemaId}
        selectedEntityLinkField={selectedEntityLinkField}
        selectedStudyItems={selectedStudyItems}
        onNameChange={setName}
        onFolderIdChange={handleFolderIdChange}
        onTagSchemaIdChange={setTagSchemaId}
        onEntityLinkFieldChange={setSelectedEntityLinkField}
        onSelectedStudyItemsChange={setSelectedStudyItems}
        entityLinkFields={entityLinkFields}
        oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
        entityLinkFieldLabel={entityLinkFieldLabel}
      />
    </Modal>
  );
}
